import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useCommandPalette } from '@/hooks/useCommandPalette';
import { getShortcutString } from '@/hooks/useKeyboardShortcut';
import { useAiAssistantStore } from '@/store/ai-assistant.store';
import { useAuthStore, hasPermission } from '@/store/auth.store';
import { useIncidentStore } from '@/store/incident.store';
import { useMyCurrentPosition } from '@/hooks/usePositions';
import { useTenantQuery } from '@/hooks/useTenantQuery';
import { NotificationBell, NotificationPanel } from '../notifications';
import { MessagingToggle } from '../messaging';
import { AdminIcon, SettingsIcon } from '../icons';
import FaIcon from '../common/FaIcon';

import StatusClock from './StatusClock';
import ConnectionStatus from './ConnectionStatus';
import AccountMenu from './AccountMenu';
import SwitchPositionModal from './SwitchPositionModal';
import GlobalTransferIndicator from '../incidents/GlobalTransferIndicator';

// Minimal slice of the current operational period consumed by the status bar.
// Backend returns either the period object or `{ data: null }` when the
// incident has no current period (see operational-period.service.ts).
interface CurrentOpPeriodSlice {
  period_number?: number;
  status?: string;
  name?: string;
}

export default function DesktopTopBar() {
  const { toggle: toggleSearch } = useCommandPalette();
  const { togglePanel: toggleAiPanel } = useAiAssistantStore();
  const user = useAuthStore(s => s.user);
  const permissions = useAuthStore(s => s.permissions);
  const { activeIncident } = useIncidentStore();
  const { data: myPosition = null } = useMyCurrentPosition(activeIncident?.id);
  const [showSwitchModal, setShowSwitchModal] = useState(false);

  // Current operational period for the engaged incident (status spine).
  const { data: opPeriodRaw } = useTenantQuery<CurrentOpPeriodSlice | { data: null } | null>(
    ['incidents', activeIncident?.id ?? 'none', 'operational-periods', 'current'],
    activeIncident
      ? `/incidents/${activeIncident.id}/operational-periods/current`
      : '/incidents/none/operational-periods/current',
    { enabled: !!activeIncident?.id, staleTime: 60_000 },
  );
  const opPeriodNumber =
    opPeriodRaw &&
    typeof opPeriodRaw === 'object' &&
    'period_number' in opPeriodRaw &&
    typeof opPeriodRaw.period_number === 'number'
      ? opPeriodRaw.period_number
      : null;

  const hasClaimedPosition = !!(activeIncident && myPosition?.position_name);

  return (
    <div className="hidden lg:flex sticky top-0 z-30 h-14 items-center gap-3 border-b border-theme-primary bg-surface-primary px-6">
      {/* Search trigger */}
      <button
        type="button"
        onClick={toggleSearch}
        aria-label="Search: open command palette"
        className="flex shrink-0 items-center gap-2 h-9 px-3 w-64 text-theme-tertiary rounded-lg border border-theme-primary hover:bg-surface-tertiary transition-colors"
      >
        <FaIcon icon="magnifying-glass" className="h-4 w-4" />
        <span className="text-sm">Search...</span>
        <kbd className="ml-auto text-xs font-medium opacity-60 border border-theme-primary rounded px-1.5 py-0.5">
          {getShortcutString('k', { ctrl: true, meta: true })}
        </kbd>
      </button>

      {/* Transfer of Command indicator */}
      <GlobalTransferIndicator />

      <div className="flex-1" />

      {/* ── Status spine ──────────────────────────────────────────────────
          Engaged incident code · OP period · claimed ICS position · clock ·
          connection state. Collapses gracefully: the clock hides first
          (below xl); below lg the whole bar yields to the mobile header. */}
      <div className="flex min-w-0 items-center gap-3">
        {activeIncident && (
          <Link
            to={`/incidents/${activeIncident.id}`}
            title={activeIncident.name}
            className="inline-flex min-w-0 items-center h-7 px-2 rounded-md border border-theme-primary bg-surface-secondary font-mono text-xs font-medium text-theme-primary hover:bg-surface-tertiary transition-colors"
          >
            <span className="truncate max-w-[180px]">{activeIncident.incident_number}</span>
          </Link>
        )}

        {activeIncident && opPeriodNumber != null && (
          <span
            className="shrink-0 font-mono text-xs text-theme-secondary"
            title={`Current operational period: OP ${opPeriodNumber}`}
          >
            OP {opPeriodNumber}
          </span>
        )}

        {/* Claimed ICS position — compact mono chip. When NO position is
            claimed, the claim affordance lives in the account dropdown
            instead of cluttering the bar. */}
        {hasClaimedPosition && (
          <div
            className="flex min-w-0 items-center gap-1 h-7 pl-2 pr-1 rounded-md border border-theme-primary"
            title={myPosition!.position_name}
          >
            <span className="font-mono text-[11px] font-medium text-theme-primary truncate max-w-[160px]">
              {myPosition!.position_code || myPosition!.position_name}
            </span>
            <button
              type="button"
              aria-label="Switch position"
              title="Switch position"
              onClick={() => setShowSwitchModal(true)}
              className="shrink-0 p-0.5 text-theme-tertiary hover:text-theme-primary"
            >
              <FaIcon icon="arrows-rotate" className="h-3 w-3" />
            </button>
          </div>
        )}

        <StatusClock className="hidden xl:inline shrink-0" />
        <ConnectionStatus className="shrink-0" />
      </div>

      {/* Divider between status spine and the utility icon cluster */}
      <div className="h-6 w-px bg-theme-primary" />

      {/* ── Utility icon cluster — tight group, uniform neutral treatment.
          AI keeps the brand accent (the only colored icon besides the
          notification badge, per the color budget). ─────────────────── */}
      <div className="flex shrink-0 items-center gap-0.5">
        {/* Settings */}
        {hasPermission('settings', 'view') && (
          <NavLink
            to="/settings"
            className={({ isActive }) =>
              `p-2 rounded-lg transition-colors ${
                isActive
                  ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-500/10'
                  : 'text-theme-secondary hover:bg-surface-tertiary hover:text-theme-primary'
              }`
            }
            title="Settings"
          >
            <SettingsIcon className="h-5 w-5" />
          </NavLink>
        )}

        {/* Admin Panel */}
        {user?.is_platform_admin && (
          <NavLink
            to="/admin"
            className={({ isActive }) =>
              `p-2 rounded-lg transition-colors ${
                isActive
                  ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-500/10'
                  : 'text-theme-secondary hover:bg-surface-tertiary hover:text-theme-primary'
              }`
            }
            title="Admin Panel"
          >
            <AdminIcon className="h-5 w-5" />
          </NavLink>
        )}

        {/* AI Assistant — brand accent per color budget */}
        <button
          type="button"
          aria-label="Open AI Assistant"
          title="Open AI Assistant"
          onClick={toggleAiPanel}
          className="p-2 rounded-lg text-primary-500 hover:bg-surface-tertiary transition-colors"
        >
          <FaIcon icon="sparkles" className="h-5 w-5" />
        </button>

        {/* Notifications */}
        <div className="relative">
          <NotificationBell className="p-2 rounded-lg text-theme-secondary hover:bg-surface-tertiary hover:text-theme-primary transition-colors" />
          <NotificationPanel position="below" />
        </div>

        {/* Messaging */}
        <MessagingToggle className="relative p-2 rounded-lg text-theme-secondary hover:bg-surface-tertiary hover:text-theme-primary transition-colors" />
      </div>

      {/* Divider before account */}
      <div className="h-6 w-px bg-theme-primary" />

      {/* Account dropdown — hosts the position claim affordance when no
          position is held for the engaged incident. */}
      <AccountMenu
        showClaimPosition={!!activeIncident && !hasClaimedPosition}
        onClaimPosition={() => setShowSwitchModal(true)}
      />

      {/* Switch Position Modal */}
      {activeIncident && (
        <SwitchPositionModal
          isOpen={showSwitchModal}
          onClose={() => setShowSwitchModal(false)}
          incidentId={activeIncident.id}
          currentPosition={myPosition?.position_name ? myPosition : null}
        />
      )}
    </div>
  );
}
